import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { OrderUtilityService } from '../../services/order-utility/order-utility.service';
import { ArcVersionMapping, ErrorMessage, OpenOrder, OrderShipment, OrderShipmentGroup } from './../../models';
import { ErrorType, LineStatus, MediaType, OrderType, SourceSystem, ShipmentResetType } from './../../models/enums/';
import { ShipmentLineReset } from './../../models';
import { map } from 'rxjs/operators';
import { CompressedOrderShipmentGroupsList } from '../../models/compressed-order-shipment-groups-list';
import { OrderShipmentGroupsList } from '../../models/order-shipment-groups-list';

@Injectable({
  providedIn: 'root'
})
export class OrderService {
  // only should be true while wanting to test large order data in dev
  // developing often doesn't have large data sets to play with
  // also double make sure this doesn't make it to production with the environment flag
  private isTestingLargeData = false;

  constructor(private readonly httpClient: HttpClient, private readonly orderUtilityServie: OrderUtilityService) { }

  public getAllOrderShipments(): Observable<OrderShipmentGroupsList> {
    const url = 'api/order';

    if (this.isTestingLargeData && !environment.production) {
      return this.generateLargeOrderResults();
    }

    // Decompress the OrderShipmentGroups from the backend before returning
    return this.httpClient
      .get<CompressedOrderShipmentGroupsList>(url)
      .pipe(map((compressedOrderShipmentGroupsList: CompressedOrderShipmentGroupsList) => CompressedOrderShipmentGroupsList.toOrderShipmentGroupsList(compressedOrderShipmentGroupsList)));
  }

  public getAllOrderShipmentsByCustomerNumber(customerNumber: number): Observable<OrderShipmentGroup[]> {
    const url = `api/order/search-by-customer-number/${customerNumber}`;
    return this.httpClient.get<OrderShipmentGroup[]>(url);
  }

  public getArcVersionMappings(): Observable<ArcVersionMapping[]> {
    const url = 'api/order/arc-version-mappings';
    return this.httpClient.get<ArcVersionMapping[]>(url);
  }

  public getOpenOrders(search: number): Observable<OpenOrder[]> {
    const url = `api/order/search-open-orders-by-order-number/${search}`;
    return this.httpClient.get<OpenOrder[]>(url);
  }

  public getOrdersByPortalGuid(portalGuid: string): Observable<OrderShipmentGroup[]> {
    const url = `api/order/search-by-portal/${portalGuid}`;
    return this.httpClient.get<OrderShipmentGroup[]>(url);
  }

  public searchAllOrdersByOrderNumber(orderNumber: number): Observable<OrderShipmentGroup[]> {
    const url = `api/order/search-all-by-order-number/${orderNumber}`;
    return this.httpClient.get<OrderShipmentGroup[]>(url);
  }

  public getOrderDetails(orderNumber: number, shipmentNumber: number, sourceSystem: SourceSystem): Observable<OrderShipment> {
    const url = `api/order/details/${orderNumber}/${shipmentNumber}/${sourceSystem}`;

    const testingOrderDetails = false;
    if (testingOrderDetails) {
      return of(this.generateAndTestOrderShipmentDetails());
    }

    return this.httpClient.get<OrderShipment>(url);
  }

  public getProductId(productNumber: string): Observable<string> {
    const url = `api/order/get-product-id/${productNumber}`;
    return this.httpClient.get<string>(url);
  }

  public resetShipment(shipmentResetType: ShipmentResetType, orderNumber: number, shipmentNumber: number, sourceSystem: SourceSystem): Observable<string> {
    let url = `api/order/reset-shipment/${orderNumber}/${shipmentNumber}/${shipmentResetType}/${sourceSystem}`;
    return this.httpClient.post<string>(url, null);
  }

  public resetShipmentLines(shipmentLineResets: ShipmentLineReset[], orderNumber: number, shipmentNumber: number, sourceSystem: SourceSystem): Observable<string> {
    let url = `api/order/reset-lines/${orderNumber}/${shipmentNumber}/${sourceSystem}`;
    return this.httpClient.post<string>(url, shipmentLineResets);
  }

  private generateLargeOrderResults(): Observable<OrderShipmentGroupsList> {
    const orderShipmentGroupsList = new OrderShipmentGroupsList();

    // auto-generate a bunch of pending orders
    const ordersToCreate = 50;
    const pendingShipments: OrderShipment[] = [];
    for (let i = 0; i < ordersToCreate; i++) {
      const shipment: OrderShipment = new OrderShipment();
      const randomizeDateAmount = Math.floor(Math.random() * 20) - 15; // -15 means some will be past ship date

      shipment.orderEntryDate = this.generateRandomFutureDate(randomizeDateAmount);
      shipment.needDate = this.generateRandomFutureDate(randomizeDateAmount + 5);
      shipment.shipDate = this.generateRandomFutureDate(randomizeDateAmount + 10); // generates up to 30 days in the future
      shipment.customerName = `Swank Test Order #${i}`;
      shipment.customerNumber = Math.floor(Math.random() * 100000);
      shipment.orderNumber = 1164544;
      shipment.shipmentNumber = i + 1;
      shipment.orderType = OrderType.Adebs; // ADEBS is 4
      shipment.mediaType = MediaType.Cvd; // CVD is 3
      shipment.status = LineStatus.Burning;
      shipment.errors = [new ErrorMessage('Sample error message', new Date(), ErrorType.PastShipDate, '')];

      pendingShipments.push(shipment);
    }
    const pendingShipGroup: OrderShipmentGroup = {
      status: 1,
      orderShipments: pendingShipments
    };
    orderShipmentGroupsList.orderShipmentGroups.push(pendingShipGroup);

    // auto-generate some that are in burning
    const burningShipments: OrderShipment[] = [];
    for (let i = 0; i < ordersToCreate; i++) {
      const shipment: OrderShipment = new OrderShipment();

      const randomizeDateAmount = Math.floor(Math.random() * 20) - 15; // -15 means some will be past ship date
      shipment.orderEntryDate = this.generateRandomFutureDate(randomizeDateAmount);
      shipment.needDate = this.generateRandomFutureDate(randomizeDateAmount + 5);
      shipment.shipDate = this.generateRandomFutureDate(randomizeDateAmount + 10);
      shipment.orderNumber = 1164544;
      shipment.customerName = `Swank Test Order #${i}`;
      shipment.shipmentNumber = i + 1;
      shipment.orderType = OrderType.StreamingServer;
      shipment.mediaType = MediaType.Electronic;
      shipment.status = LineStatus.Transcoding;
      shipment.errors = [new ErrorMessage('Sample error message', new Date(), ErrorType.PastShipDate, '')];

      burningShipments.push(shipment);
    }

    const burningShipGroup: OrderShipmentGroup = {
      status: LineStatus.Transcoding,
      orderShipments: burningShipments
    };
    orderShipmentGroupsList.orderShipmentGroups.push(burningShipGroup);

    return of(orderShipmentGroupsList);
  }

  private generateRandomFutureDate(amount: number): Date {
    const newDate = new Date();
    newDate.setDate(newDate.getDate() + amount);
    return newDate;
  }

  private generateAndTestOrderShipmentDetails(): OrderShipment {
    return this.orderUtilityServie.convertDateStringsToDatesForJSON(testOrderDetailsData) as OrderShipment;
  }
}

// To get this test data, just copy the result from a "higher" environment like production which will have fresh data
// the JSON response will probably turn things into double quotes. The linter should give options to fix all these quote issues
// if you right click on the issue.
export const testOrderDetailsData = {
  orderNumber: 1778662,
  customerNumber: 347197,
  shipmentNumber: 1,
  shipDate: '2021-02-18T00:00:00',
  needDate: '2021-03-01T00:00:00',
  orderEntryDate: '2021-02-08T00:00:00',
  orderType: 4,
  status: 0,
  hasError: true,
  customerName: 'WORLD DREAM                   ',
  drmType: '',
  stream: 'AVI                                                                             ',
  broadcast: null,
  digitalProfile: 0,
  digitalProfileDescription: 'SINGLE LAYER NTSC CVD                             ',
  accountExecutive: 'Stephanie Breitenstein        ',
  errors: [
    {
      nessage: 'Shipment is past ship date (2/18/2021)',
      time: '2021-02-19T00:00:00',
      type: 2
    }
  ],
  mediaType: 3,
  lines: [
    {
      orderNumber: 1778662,
      lineNumber: 4,
      title: 'FINDING DORY',
      productNumber: '0054749',
      status: 11,
      statusDates: {
        3: {
          startDate: '2021-02-16T15:04:30.823',
          endDate: '2021-02-16T15:43:34.76'
        },
        4: {
          startDate: null,
          endDate: null
        },
        5: {
          startDate: '2021-02-16T15:46:47.95',
          endDate: '2021-02-16T16:04:00.237'
        },
        6: {
          startDate: '2021-02-16T16:04:00.72',
          endDate: '2021-02-16T16:10:21.52'
        },
        9: {
          startDate: '2021-02-16T16:12:13.36',
          endDate: '2021-02-16T16:21:48.48'
        }
      },
      isProcessing: false,
      errors: [],
      hasError: false,
      encryptStation: null,
      isHd: true,
      version: 'YS',
      sortPriority: 0,
      dRMType: ''
    },
    {
      orderNumber: 1778662,
      lineNumber: 3,
      title: 'THE LEGO BATMAN MOVIE',
      productNumber: '0057011',
      status: 11,
      statusDates: {
        3: {
          startDate: '2021-02-16T15:44:39.023',
          endDate: '2021-02-16T16:27:00.38'
        },
        4: {
          startDate: null,
          endDate: null
        },
        5: {
          startDate: '2021-02-16T16:27:03.363',
          endDate: '2021-02-16T16:33:19.843'
        },
        6: {
          startDate: '2021-02-16T16:33:20.483',
          endDate: '2021-02-16T16:38:33.88'
        },
        9: {
          startDate: '2021-02-16T16:39:19.153',
          endDate: '2021-02-16T16:48:58.39'
        }
      },
      isProcessing: false,
      errors: [],
      hasError: false,
      encryptStation: null,
      isHd: true,
      version: 'CS',
      sortPriority: 1,
      dRMType: ''
    },
    {
      orderNumber: 1778662,
      lineNumber: 1,
      title: 'WONDER PARK',
      productNumber: '0058505',
      status: 11,
      statusDates: {
        3: {
          startDate: '2021-02-16T15:34:35.863',
          endDate: '2021-02-16T16:05:29.247'
        },
        4: {
          startDate: null,
          endDate: null
        },
        5: {
          startDate: '2021-02-16T16:05:31.603',
          endDate: '2021-02-16T16:43:40.597'
        },
        6: {
          startDate: '2021-02-16T16:43:41.52',
          endDate: '2021-02-16T16:50:04.74'
        },
        9: {
          startDate: '2021-02-16T16:51:57.903',
          endDate: '2021-02-16T17:00:53.917'
        }
      },
      isProcessing: false,
      errors: [],
      hasError: false,
      encryptStation: null,
      isHd: true,
      version: 'YS',
      sortPriority: 2,
      dRMType: ''
    },
    {
      orderNumber: 1778662,
      lineNumber: 5,
      title: 'THE SECRET LIFE OF PETS 2',
      productNumber: '0058514',
      status: 11,
      statusDates: {
        3: {
          startDate: '2021-02-16T16:09:40.687',
          endDate: '2021-02-16T16:39:50.197'
        },
        4: {
          startDate: null,
          endDate: null
        },
        5: {
          startDate: '2021-02-16T16:39:52.64',
          endDate: '2021-02-16T16:45:57.677'
        },
        6: {
          startDate: '2021-02-16T16:45:58.1',
          endDate: '2021-02-16T16:54:15.22'
        },
        9: {
          startDate: '2021-02-16T16:56:29.99',
          endDate: '2021-02-16T17:05:25.16'
        }
      },
      isProcessing: false,
      errors: [],
      hasError: false,
      encryptStation: null,
      isHd: true,
      version: 'YS',
      sortPriority: 3,
      dRMType: ''
    },
    {
      orderNumber: 1778662,
      lineNumber: 2,
      title: 'TROLLS WORLD TOUR',
      productNumber: '0061730',
      status: 11,
      statusDates: {
        3: {
          startDate: '2021-02-16T15:39:37.31',
          endDate: '2021-02-16T16:10:57.517'
        },
        4: {
          startDate: null,
          endDate: null
        },
        5: {
          startDate: '2021-02-16T16:11:00.067',
          endDate: '2021-02-16T16:17:32.39'
        },
        6: {
          startDate: '2021-02-16T16:17:32.45',
          endDate: '2021-02-16T16:25:59.033'
        },
        9: {
          startDate: '2021-02-16T16:26:45.847',
          endDate: '2021-02-16T16:36:08.307'
        }
      },
      isProcessing: false,
      errors: [],
      hasError: false,
      encryptStation: null,
      isHd: true,
      version: 'YS',
      sortPriority: 4,
      dRMType: ''
    }
  ],
  digitalSequenceNumber: 2,
  sourceSystem: 1,
  burnStation: 'Rimageprod                                        ',
  trackingCodeInfo: null,
  shippingAddress: 'C/O UNION SERVICES PTE LTD     NO.7 AIRLINE ROAD #02-07       CARGO AGENTS BUILDING E       ',
  shippingCity: 'SINGAPORE           ',
  shippingState: '  ',
  shippingZip: '819834',
  shippingCountry: 'SI  ',
  uploadNonEDLShipmentToNetwork: false
};
